import { z } from 'zod';

/**
 * Listen des Baustellenberichts (Blöcke I/J der Vorlage „PROBIG Baustelle",
 * ADR 0070 Schritt 5) und die Baustellenfotos.
 *
 * Eine Tabelle mit `type` statt zehn Textfeldern am Bericht: Störungen und
 * fehlende Teile sind **Listen** — man zählt sie, hakt sie ab und hängt Fotos
 * daran. Als Absatz im Bericht ginge all das verloren.
 */

/**
 * Die Listen der Vorlage. `withAssignee` steuert, ob die Oberfläche nach
 * Verantwortlichem und Frist fragt — bei einer Kundeneinschätzung wäre das
 * sinnlos, bei „zu erledigen" ist es der Kern.
 */
export const REPORT_ITEM_TYPES = [
  { value: 'STOERUNG', label: 'Störung', plural: 'Störungen', withAssignee: true },
  { value: 'FEHLENDES_TEIL', label: 'Fehlendes Teil', plural: 'Fehlende Teile', withAssignee: true },
  { value: 'ZU_ERLEDIGEN', label: 'Zu erledigen', plural: 'Zu erledigen', withAssignee: true },
  { value: 'BERICHT', label: 'Bericht', plural: 'Bericht', withAssignee: false },
  { value: 'INFO', label: 'Info', plural: 'Infos', withAssignee: false },
  { value: 'FREMDFIRMA', label: 'Fremdfirma vor Ort', plural: 'Fremdfirmen vor Ort', withAssignee: false },
  { value: 'VERTRETENE_FIRMA', label: 'Vertretene Firma', plural: 'Vertretene Firmen', withAssignee: false },
  {
    value: 'KUNDENEINSCHAETZUNG',
    label: 'Kundeneinschätzung',
    plural: 'Kundeneinschätzung',
    withAssignee: false,
  },
  { value: 'DISKUSSIONSTHEMA', label: 'Diskussionsthema', plural: 'Diskussionsthemen', withAssignee: false },
  { value: 'VERTRIEBSINFO', label: 'Info für den Vertrieb', plural: 'Infos für den Vertrieb', withAssignee: false },
] as const;

export type ReportItemType = (typeof REPORT_ITEM_TYPES)[number]['value'];

const ITEM_TYPE_VALUES: readonly string[] = REPORT_ITEM_TYPES.map((entry) => entry.value);

/** Beschriftung eines Listentyps; unbekannte Werte werden roh durchgereicht. */
export function reportItemTypeLabel(value: string): string {
  return REPORT_ITEM_TYPES.find((entry) => entry.value === value)?.label ?? value;
}

/** Überschrift der Liste (Mehrzahl), für Formular und PDF. */
export function reportItemTypePlural(value: string): string {
  return REPORT_ITEM_TYPES.find((entry) => entry.value === value)?.plural ?? value;
}

/** Fragt dieser Listentyp nach Verantwortlichem und Frist? */
export function reportItemHasAssignee(value: string): boolean {
  return REPORT_ITEM_TYPES.find((entry) => entry.value === value)?.withAssignee ?? false;
}

export const assemblyReportItemInputSchema = z.object({
  type: z.string().refine((value) => ITEM_TYPE_VALUES.includes(value), { message: 'Unbekannte Liste.' }),
  text: z.string().trim().min(1, 'Der Eintrag darf nicht leer sein.').max(4000),
  assignee: z.string().trim().max(200).nullish(),
  dueDate: z.string().datetime().nullish(),
  isDone: z.boolean().optional(),
});
export type AssemblyReportItemInput = z.infer<typeof assemblyReportItemInputSchema>;

/** Ändern: alles optional (Auto-Save je Feld) — der Typ bleibt, wie er ist. */
export const updateAssemblyReportItemSchema = assemblyReportItemInputSchema.omit({ type: true }).partial();
export type UpdateAssemblyReportItemInput = z.infer<typeof updateAssemblyReportItemSchema>;

export const assemblyReportItemResponseSchema = z.object({
  id: z.string().uuid(),
  type: z.string(),
  text: z.string(),
  assignee: z.string().nullable(),
  dueDate: z.string().datetime().nullable(),
  isDone: z.boolean(),
  position: z.number().int(),
});
export type AssemblyReportItemResponse = z.infer<typeof assemblyReportItemResponseSchema>;

export const assemblyReportPhotoResponseSchema = z.object({
  id: z.string().uuid(),
  /** Zu welchem Listen-Eintrag das Foto gehört (z. B. Schadensbild). */
  reportItemId: z.string().uuid().nullable(),
  fileName: z.string(),
  caption: z.string().nullable(),
  position: z.number().int(),
  createdAt: z.string().datetime(),
});
export type AssemblyReportPhotoResponse = z.infer<typeof assemblyReportPhotoResponseSchema>;

/** Metadaten beim Hochladen — die Bytes stecken im Rohkörper (Muster Picking). */
export const uploadAssemblyReportPhotoSchema = z.object({
  reportItemId: z.string().uuid().nullish(),
  caption: z.string().trim().max(500).nullish(),
});
export type UploadAssemblyReportPhotoInput = z.infer<typeof uploadAssemblyReportPhotoSchema>;

/** Bildunterschrift nachtragen. */
export const updateAssemblyReportPhotoSchema = z.object({
  caption: z.string().trim().max(500).nullish(),
});
export type UpdateAssemblyReportPhotoInput = z.infer<typeof updateAssemblyReportPhotoSchema>;

/** Eine Liste mit ihren Einträgen — so gruppiert die Oberfläche und das PDF. */
export interface ReportItemGroup {
  type: string;
  label: string;
  plural: string;
  withAssignee: boolean;
  items: AssemblyReportItemResponse[];
  /** Offene Einträge — nur bei Listen mit Verantwortlichem aussagekräftig. */
  openCount: number;
}

/**
 * Einträge nach Liste bündeln, in der Reihenfolge von `REPORT_ITEM_TYPES`.
 * Leere Listen fallen raus: ein Bericht mit zehn leeren Überschriften wäre
 * weder auf dem iPad noch im PDF lesbar.
 */
export function groupReportItems(items: readonly AssemblyReportItemResponse[]): ReportItemGroup[] {
  return REPORT_ITEM_TYPES.map((entry) => {
    const own = items.filter((item) => item.type === entry.value);
    return {
      type: entry.value,
      label: entry.label,
      plural: entry.plural,
      withAssignee: entry.withAssignee,
      items: own,
      openCount: own.filter((item) => !item.isDone).length,
    };
  }).filter((group) => group.items.length > 0);
}

/**
 * Wie viele Einträge noch offen sind — die Zahl, die den Bericht am Ende
 * „unfertig" macht. Gezählt werden nur Listen mit Verantwortlichem; eine
 * Kundeneinschätzung ist nie „erledigt".
 */
export function countOpenReportItems(items: readonly AssemblyReportItemResponse[]): number {
  return items.filter((item) => !item.isDone && reportItemHasAssignee(item.type)).length;
}
